import { RightOutlined } from '@ant-design/icons'
import type { ViewFieldsFragment } from '@salescore/client-api'
import { HandleQuery, ModelIconLabel, useMe } from '@salescore/client-common'
import type {
  CompiledFormViewConfig,
  CompiledKpiPivotViewConfig,
  CompiledKpiViewConfig,
  CompiledSheetViewConfig,
  NodePath,
  ViewQueryList,
} from '@salescore/core'
import { canUseOrganizationLicenseForModel } from '@salescore/features'
import { type GetRef, Select, Space } from 'antd'
import { Array, pipe } from 'effect'
import { isNotNullish } from 'effect/Predicate'
import { t } from 'i18next'
import { type ReactNode, useEffect, useMemo, useRef, useState } from 'react'
import { CSSTransition, TransitionGroup } from 'react-transition-group'
import { useRecoilValue, useSetRecoilState } from 'recoil'

import type { RSheetColumn, RSheetRecordNode } from '../../../domain/column.ts'
import { viewsContextAtom } from '../../../recoil/view/atoms'
import { useGetModel } from '../../../recoil/view/selectors/connectionsSelector'
import { configAtom, useSetRootNodeMutation, useViewConfigSheet } from '../../../recoil/viewConfig'
import { useResolveConfigFields } from '../../../rsheet/hooks/useResolveConfigFields'
import { flatNodes } from '../../../state/nodeUtility'
import { PropertySelectorTableForContextMenuOptionOrder } from '../common'
import { addViewConfigChildNode } from './addViewConfigChildNode'
import { TABLE_HEIGHT } from './PropertySelectorTableForContextMenu/const'
import { PropertySelectorTable } from './PropertySelectorTableForContextMenu/PropertySelectorTable'
import { isSystemModel } from './PropertySelectorTableForContextMenu/PropertySelectorTableBodyForParentChildrenNode'

// レンダリングのパフォーマンスが悪いため、一部に制限する
// eslint-disable-next-line sonarjs/todo-tag
// TODO: virtualizeして良い感じにやりたい
const HEADER_HEIGHT = 160

export const PropertySelectorTableForContextMenu = ({
  initialNodePath,
  configIndex,
  columns,
  compiledConfig,
  view,
  query,
  setHasChangeToViewSchema,
}: {
  initialNodePath: NodePath
  configIndex: number | undefined
  columns: Array<RSheetColumn<RSheetRecordNode>>
  compiledConfig: CompiledSheetViewConfig | CompiledKpiViewConfig | CompiledKpiPivotViewConfig | CompiledFormViewConfig
  view: ViewFieldsFragment
  query: ViewQueryList
  setHasChangeToViewSchema: (hasChangeToViewSchema: boolean) => void
}): ReactNode => {
  const me = useMe()
  const config = useViewConfigSheet()
  const setConfig = useSetRecoilState(configAtom)
  const flattenNodes = useMemo(() => flatNodes(config.tree), [config.tree])
  const getModel = useGetModel()
  const { models } = useRecoilValue(viewsContextAtom)

  const resolveConfigFields = useResolveConfigFields(configIndex)
  const setRootNode = useSetRootNodeMutation()
  const [currentNodePath, setCurrentNodePath] = useState(initialNodePath)

  // モーダルの初期表示で Select にフォーカスを当てる
  const selectReference = useRef<GetRef<typeof Select>>(null)
  useEffect(() => selectReference.current?.focus(), [])

  const sortedModels = pipe(models, Array.sort(PropertySelectorTableForContextMenuOptionOrder))

  if (currentNodePath.length === 0 || config.tree === undefined) {
    return (
      <div data-e2e="property-selector-table-for-context-menu-object-selection">
        <div className="mb-4">{t(`最初に、追加したいオブジェクトを選択してください。`)}</div>
        <Select
          ref={selectReference}
          className="w-full"
          showSearch
          filterOption={
            // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
            (input, option) => ((option?.key ?? '') as string).toLowerCase().includes(input.toLowerCase())
          }
          onChange={(value) => {
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            const model = models.find((x) => x.name === value)!
            // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition,sonarjs/different-types-comparison
            if (model !== undefined) {
              setRootNode({
                model,
                onSet(node) {
                  setCurrentNodePath([node.name])
                },
              })
              setHasChangeToViewSchema(true)
            }
          }}
        >
          {sortedModels
            .filter((x) => !isSystemModel(x.name))
            .filter((x) => canUseOrganizationLicenseForModel(me.myUser, x.name))
            .map((model) => (
              <Select.Option value={model.name} key={[model.name, model.label].join('-')}>
                <Space>
                  <ModelIconLabel model={model} />
                </Space>
              </Select.Option>
            ))}
        </Select>
      </div>
    )
  }

  const currentNodes = currentNodePath
    .map((nodeName) => flattenNodes.find((x) => x.name === nodeName))
    .filter(isNotNullish)

  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const modelLabel: string = getModel(currentNodes.at(-1)!.modelName)?.label ?? t(`(削除されたモデル)`)

  return (
    <div
      className="relative overflow-y-auto"
      style={{ height: TABLE_HEIGHT + HEADER_HEIGHT + 13 }} // 項目表示でスクロールバーが出るため +13px
      data-e2e="property-selector-table-for-context-menu"
    >
      <div className="absolute flex w-full flex-col bg-white">
        <div className="mb-2 flex-none text-xs text-gray-500">
          {currentNodes.map((node, index) => (
            <span
              key={index}
              // eslint-disable-next-line sonarjs/todo-tag
              // TODO: ちゃんと戻れるようにする
              onClick={() => {
                if (currentNodePath.length > 1) {
                  setCurrentNodePath((paths) => paths.slice(0, -1))
                }
              }}
              className={currentNodePath.length > 1 ? 'cursor-pointer' : ''}
            >
              {getModel(node.modelName)?.label ?? t(`(削除されたモデル)`)}{' '}
              {index !== currentNodes.length - 1 && <RightOutlined />}
            </span>
          ))}{' '}
        </div>
        <div className="mb-2 flex-none font-bold">{t(`{{label}}に項目を追加`, { label: modelLabel })}</div>

        <TransitionGroup className="flex-1">
          {currentNodes.map((node, index) => (
            <CSSTransition
              key={node.name}
              in={true}
              timeout={600}
              appear={true}
              classNames={index === 0 ? '' : 'slide-left-property-context-menu'}
              unmountOnExit={true}
            >
              <HandleQuery>
                <PropertySelectorTable
                  style={{
                    ...(index < currentNodes.length - 1
                      ? {
                          visibility: 'hidden',
                          transition: 'visibility 0s 0.3s linear',
                        }
                      : {
                          visibility: 'visible',
                        }),
                  }}
                  node={node}
                  parentNode={currentNodes[index - 1]}
                  addNodes={({ toggleNodeProperties, referenceToProperty, option }) => {
                    const childAdded =
                      referenceToProperty === undefined
                        ? undefined
                        : addViewConfigChildNode({
                            config,
                            node,
                            referenceToProperty,
                          })
                    setConfig({
                      ...config,
                      tree: childAdded?.tree ?? config.tree, // undefinedに更新しないよう、undefined時はconfigの値を採用する
                      fields:
                        toggleNodeProperties === undefined
                          ? config.fields
                          : resolveConfigFields({ config, toggleNodeProperties }),
                    })
                    setHasChangeToViewSchema(true)
                    if (childAdded !== undefined && option?.skipSetCurrentNodePath !== true) {
                      setCurrentNodePath((xs) => [...xs, childAdded.newNodeName])
                    }
                  }}
                  columns={columns}
                  compiledConfig={compiledConfig}
                  view={view}
                  query={query}
                />
              </HandleQuery>
            </CSSTransition>
          ))}
        </TransitionGroup>
      </div>
    </div>
  )
}
